import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import api from "../api"
import { MAWBModel, PalletModel } from "../api/linehaul.api"

export interface LineHaulState {
    countriesWithCounts: Record<string, any>[]
    pallets: Record<string, any>
    bags: Record<string, any>
    bags_transit: {
        data: Record<string, any>
        loading: boolean
        error: any
    }
    totalPallets: number
    pallet: PalletModel
    mawbs: Record<string, any>
    totalMawbs: number
    mawb: MAWBModel
    readyLHParcels: Record<string, any>
    loading: boolean
    tab: string
    country: string
    allPalletsloading: boolean
    allPallets: Record<string, any>
    error?: string
    success?: boolean
    attachments: {
        list: any[]
        loading: boolean
        error: any
    }
    editingPallet?: any
    editingBag?: any
    exporting: boolean
    statuses: any[]
    statusesLoading: boolean
    destinationsList: { id: number; iso2_code: string; name: string }[]
    destinationsLoading: boolean
    destinationsError: any
}

const initialState: LineHaulState = {
    countriesWithCounts: [],
    pallets: {},
    bags: {},
    bags_transit: {
        data: {},
        loading: false,
        error: undefined
    },
    totalPallets: 0,
    pallet: {},
    mawbs: {},
    totalMawbs: 0,
    mawb: {},
    readyLHParcels: {},
    loading: false,
    allPalletsloading: false,
    allPallets: {},
    tab: "bags",
    country: "AE",
    attachments: {
        list: [],
        loading: false,
        error: ""
    },
    exporting: false,
    statuses: [],
    statusesLoading: false,
    destinationsError: "",
    destinationsList: [],
    destinationsLoading: false
}

export const loadCountries = createAsyncThunk(
    "linehaul/countries/list",
    async ({ warehouse }: { warehouse?: string }, thunkAPI) => {
        try {
            const response = await api.linehaul.countriesWithCount({ warehouse })

            return response
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const loadMAWBAttachmentTypes = createAsyncThunk("linehaul/attachments/types", async () => {
    try {
        const response = await api.linehaul.getMAWBAttachmentTypes()
        return response
    } catch (error: any) {
        console.log(error)
        return error.message
    }
})

export const loadMAWBs = createAsyncThunk(
    "linehaul/mawbs/list",
    async (
        {
            warehouse,
            destination,
            page,
            size,
            keyword,
            from,
            to,
            status
        }: {
            warehouse: string
            destination: string
            page: number
            size: number
            keyword: string
            from?: string
            to?: string
            status?: string
        },
        thunkAPI
    ) => {
        try {
            const response = await api.linehaul.MAWBsList({
                warehouse,
                destination,
                page,
                size,
                keyword,
                from,
                to,
                status
            })

            return response
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const loadPallets = createAsyncThunk(
    "linehaul/pallets/list",
    async (
        {
            warehouse,
            destination,
            page,
            size,
            keyword,
            from,
            to
        }: {
            warehouse: string
            destination: string
            page: number
            size: number
            keyword: string
            from?: string
            to?: string
        },
        thunkAPI
    ) => {
        try {
            const response = await api.linehaul.palletsList({
                warehouse,
                destination,
                page,
                size,
                keyword,
                from,
                to
            })

            return response
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const loadBags = createAsyncThunk(
    "linehaul/bags/list",
    async (
        {
            warehouse,
            destination,
            page,
            size,
            keyword
        }: {
            warehouse: string
            destination: string
            page: number
            size: number
            keyword: string
        },
        thunkAPI
    ) => {
        try {
            const response = await api.linehaul.bagsList({
                warehouse,
                destination,
                page,
                size,
                keyword
            })

            return response
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const loadBagsTransit = createAsyncThunk(
    "linehaul/bags/list/transit",
    async (
        {
            warehouse,
            destination
        }: {
            warehouse: string
            destination: string
        },
        thunkAPI
    ) => {
        try {
            const response = await api.linehaul.transitBagsList({
                warehouse,
                destination
            })

            return response
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const loadParcels = createAsyncThunk(
    "linehaul/parcels/list",
    async (
        { warehouse, destination }: { warehouse?: string; destination?: string; page?: number; size?: number },
        thunkAPI
    ) => {
        try {
            const response = await api.linehaul.readyLHParcelsList({ warehouse, destination })

            return response
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const loadMAWB = createAsyncThunk(
    "linehaul/mawb/details",
    async ({ mawb }: { mawb?: string }, thunkAPI) => {
        try {
            if (mawb) {
                const response = await api.linehaul.MAWBDetails({ mawb })
                return response
            }
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const loadPallet = createAsyncThunk(
    "linehaul/pallet/details",
    async ({ palletId }: { palletId?: string }, thunkAPI) => {
        try {
            if (palletId) {
                const response = await api.linehaul.palletDetails({ palletId })
                return response
            }
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const loadAllPallets = createAsyncThunk(
    "linehaul/pallets/all",
    async (
        { warehouse, destination, keyword }: { warehouse: string; destination: string; keyword: string },
        thunkAPI
    ) => {
        try {
            const response = await api.linehaul.allPalletsList({ warehouse, destination, keyword })

            return response
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const loadStatuses = createAsyncThunk(
    "linehaul/statuses",
    async ({ warehouse }: { warehouse: string }, thunkAPI) => {
        try {
            const response = await api.linehaul.LineHaulStatuses({ warehouse })
            return response
        } catch (error: any) {
            console.log(error)
            return error.message
        }
    }
)

export const exportList = createAsyncThunk(
    "linehaul/export",
    async (args: {
        warehouse: string
        keyword?: string
        from?: string
        to?: string
        key: "lhMawbs" | "lhPallets" | "lhBags"
        status?: string
    }) => {
        const response = await api.linehaul.exportList(args)
        return response
    }
)

export const loadDestinations = createAsyncThunk("linehaul/destinations", async () => {
    const response = await api.linehaul.destinations()
    return response
})

export const linehaulSlice = createSlice({
    name: "linehaul",
    initialState,

    reducers: {
        clearError: (state: LineHaulState) => {
            state.error = undefined
        },
        resetSuccess: (state: LineHaulState, action: PayloadAction<any>) => {
            state.success = action.payload
        },
        setTab: (state: LineHaulState, action: PayloadAction<any>) => {
            state.tab = action.payload
        },
        setSelectedCountry: (state: LineHaulState, action: PayloadAction<any>) => {
            state.country = action.payload
        },
        editPallet: (state: LineHaulState, action: PayloadAction<any>) => {
            state.editingPallet = action.payload
        },
        editBag: (state: LineHaulState, action: PayloadAction<any>) => {
            state.editingBag = action.payload
        }
    },

    extraReducers: {
        [loadCountries.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadCountries.rejected.toString()]: (state, action) => {
            state.loading = false
            state.error = action.payload?.data?.error || ""
        },
        [loadCountries.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.countriesWithCounts = action.payload?.data?.payload || []
        },
        [loadDestinations.pending.toString()]: (state, action) => {
            state.destinationsLoading = true
        },
        [loadDestinations.rejected.toString()]: (state, action) => {
            state.destinationsLoading = false
            state.destinationsError = action.payload?.data?.error || "Unable to load destinations"
        },
        [loadDestinations.fulfilled.toString()]: (state, action) => {
            state.destinationsLoading = false
            state.destinationsList = action.payload?.data?.payload || []
        },
        [loadMAWBs.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadMAWBs.rejected.toString()]: (state, action) => {
            state.loading = false
            state.error = action.payload?.data?.error || ""
        },
        [loadMAWBs.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.mawbs = action.payload?.data?.payload || {}
        },

        [loadMAWB.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadMAWB.rejected.toString()]: (state, action) => {
            state.loading = false
            state.error = action.payload?.data?.error || ""
        },
        [loadMAWB.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.mawb = action.payload?.data?.payload || {}
        },

        [loadPallets.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadPallets.rejected.toString()]: (state, action) => {
            state.loading = false
            state.error = action.payload?.data?.error || ""
        },
        [loadPallets.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.pallets = action.payload?.data?.payload || {}
        },

        [loadBags.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadBags.rejected.toString()]: (state, action) => {
            state.loading = false
            state.error = action.payload?.data?.error || ""
        },
        [loadBags.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.bags = action.payload?.data?.payload || {}
        },

        [loadBagsTransit.pending.toString()]: (state, action) => {
            state.bags_transit.loading = true
        },
        [loadBagsTransit.rejected.toString()]: (state, action) => {
            state.bags_transit.loading = false
            state.bags_transit.error = action.payload?.data?.error || ""
        },
        [loadBagsTransit.fulfilled.toString()]: (state, action) => {
            state.bags_transit.loading = false
            state.bags_transit.data = action.payload?.data?.payload || {}
        },

        [loadPallet.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadPallet.rejected.toString()]: (state, action) => {
            state.loading = false
            state.error = action.payload?.data?.error || ""
        },
        [loadPallet.fulfilled.toString()]: (state, action) => {
            state.loading = false
            state.pallet = action.payload?.data?.payload || {}
        },

        [loadParcels.pending.toString()]: (state, action) => {
            state.loading = true
        },
        [loadParcels.rejected.toString()]: (state, action) => {
            state.loading = false
            state.error = action.payload?.data?.error || ""
        },
        [loadParcels.fulfilled.toString()]: (state, action) => {
            state.loading = false
            //state.success = true
            state.readyLHParcels = action.payload?.data?.payload || {}
        },

        [loadAllPallets.pending.toString()]: (state, action) => {
            state.allPalletsloading = true
        },
        [loadAllPallets.rejected.toString()]: (state, action) => {
            state.allPalletsloading = false
            state.error = action.payload?.data?.error || ""
        },
        [loadAllPallets.fulfilled.toString()]: (state, action) => {
            state.allPalletsloading = false
            state.allPallets = action.payload?.data?.payload || {}
        },
        [loadMAWBAttachmentTypes.pending.toString()]: (state, action) => {
            state.attachments.loading = true
        },
        [loadMAWBAttachmentTypes.rejected.toString()]: (state, action) => {
            state.attachments.loading = false
            state.attachments.error = action.payload?.data?.error || ""
        },
        [loadMAWBAttachmentTypes.fulfilled.toString()]: (state, action) => {
            state.attachments.loading = false
            state.attachments.list = action.payload?.data?.payload || []
        },
        [exportList.pending.toString()]: (state, action) => {
            state.exporting = true
        },
        [exportList.rejected.toString()]: (state, action) => {
            state.exporting = false
        },
        [exportList.fulfilled.toString()]: (state, action) => {
            state.exporting = false
        },
        [loadStatuses.pending.toString()]: (state, action) => {
            state.statusesLoading = true
        },
        [loadStatuses.rejected.toString()]: (state, action) => {
            state.statusesLoading = false
        },
        [loadStatuses.fulfilled.toString()]: (state, action) => {
            state.statusesLoading = false
            state.statuses = action.payload?.data?.payload || []
        }
    }
})

export const { clearError, resetSuccess, setTab, setSelectedCountry, editPallet, editBag } =
    linehaulSlice.actions

export default linehaulSlice.reducer
